import * as _ from "lodash";
import { NormalizedSchema, schema } from "normalizr";
import { CamelCase } from "type-fest";

import chartSchemas from "../chart/schemas";
import { ChartEntity, ChartList } from "../chart";

import {
  DataDescriptorEntity,
  DataDescriptorList,
  MetricCollection,
  MetricEntity,
  MetricVariantResponse
} from "./typings";

const metricVariant = new schema.Entity(
  "dataDescriptor",
  {
    supportedCharts: new schema.Array(chartSchemas.metricDefined)
  },
  {
    processStrategy: (
      value: MetricVariantResponse,
      { category }: MetricEntity
    ): Omit<DataDescriptorEntity, "supportedCharts"> & {
      supportedCharts: Array<any>;
      // remove this on the reducer as this is only needed to make the chart schema work
      __source: MetricVariantResponse;
    } => {
      const {
        id,
        name,
        type,
        dataSourceTypes,
        valueSettings: { unit, decimals, quantity, scoringMethod, min, max, conversions },
        features: {
          projection,
          analytics: { charts, displayLagDays, combinedDescriptors },
          gameplan,
          scorecard,
          kpiInsight,
          askIda
        },
        supportedFarms,
        flags
      } = value;

      const { isBeta } = _.defaultTo(flags, {} as MetricVariantResponse["flags"]);
      const { effect, subjectType } = _.defaultTo(askIda, {}) as MetricVariantResponse["features"]["askIda"];
      const isImperialSupported = _.some(conversions, (conversion) => conversion.name === "Imperial");
      const isAskIdaSupported = !_.isEmpty(askIda);

      const { maxProjectionInterval } = _.defaultTo(projection, {}) as NonNullable<
        MetricVariantResponse["features"]["projection"]
      >;

      return {
        __source: value,
        id,
        description: "",
        supportedCharts: _.map(charts, (chartName: string) => ({
          id: `${id}-${chartName}`,
          view: chartName
        })),
        supportedFarms,
        dataProperties: {
          ...(askIda
            ? {
                askIdaMappings: {
                  effect: effect,
                  subjectType: subjectType
                }
              }
            : {}),
          ...(combinedDescriptors
            ? {
                combinedDescriptors: _.values(combinedDescriptors)
              }
            : {}),
          category, // legacy TODO remove (because we will use the category from the metric)
          type,
          sourceTypes: dataSourceTypes,
          typeName: name
        },
        valueProperties: {
          maxProjectionInterval,
          acquisitionMethod: scoringMethod,
          decimals,
          max,
          min,
          offset: displayLagDays,
          type: _.defaultTo(quantity, null),
          units: _.reduce(
            _.defaultTo(conversions, []),
            (acc, { name, unit }) => {
              acc[_.toLower(name) as CamelCase<typeof name>] = unit;

              return acc;
            },
            { si: unit } as DataDescriptorEntity["valueProperties"]["units"]
          )
        },
        flags: {
          isBeta,
          isImperialSupported,
          isAskIdaSupported,
          isProjectionSupported: !_.isEmpty(projection),
          isKPIInsight: kpiInsight,
          isGamePlanKPI: gameplan,
          isScorecardSupported: scorecard
        }
      };
    }
  }
);

const metric = new schema.Entity("metric", {
  variants: new schema.Array(metricVariant)
});

const metricList = new schema.Array(metric);

export type NormalizedDataDictionary = NormalizedSchema<
  {
    chart: Record<ChartEntity["id"], ChartEntity>;
    dataDescriptor: DataDescriptorList;
  },
  Array<DataDescriptorEntity["id"]>
>;

export type NormalizedDataDescriptor = NormalizedSchema<
  {
    chart: Record<ChartEntity["id"], ChartEntity>;
    dataDescriptor: DataDescriptorList;
  },
  DataDescriptorEntity["id"]
>;

export type NormalizedMetricCollection = NormalizedSchema<
  {
    metric: MetricCollection;
    chart: ChartList;
    dataDescriptor: DataDescriptorList;
  },
  Array<MetricEntity["id"]>
>;

export type NormalizedMetric = NormalizedSchema<
  {
    metric: MetricCollection;
    chart: ChartList;
    dataDescriptor: DataDescriptorList;
  },
  MetricEntity["id"]
>;

export default {
  metric,
  metricList,
  metricVariant
};
